import type { FigureColour, FigureShape } from '../generators/figures.ts'

/** Fixed figure palette (EXAM.md §3) — part of the puzzle, identical across
 * themes; only the surrounding chrome changes. A neutral stroke keeps the
 * white and black figures visible on both light and dark backgrounds. */
const COLOUR_HEX: Record<FigureColour, string> = {
  blue: '#2563eb',
  teal: '#0d9488',
  orange: '#f97316',
  yellow: '#eab308',
  pink: '#ec4899',
  white: '#f8fafc',
  black: '#111827',
}

const STROKE = '#94a3b8'

function Star({ colour }: { colour: FigureColour }) {
  const hex = COLOUR_HEX[colour]
  return (
    <>
      <polygon
        points="10,1 19,10 10,19 1,10"
        fill="none"
        stroke={hex}
        strokeWidth="1.5"
      />
      <polygon points="10,4.5 11.6,8.4 15.5,10 11.6,11.6 10,15.5 8.4,11.6 4.5,10 8.4,8.4" fill={hex} />
    </>
  )
}

function Triangle({ colour }: { colour: FigureColour }) {
  return <polygon points="10,3 17,16 3,16" fill={COLOUR_HEX[colour]} stroke={STROKE} strokeWidth="0.8" />
}

function Arrow({ colour }: { colour: FigureColour }) {
  return (
    <polygon
      points="10,2 16,9 13,9 13,17 7,17 7,9 4,9"
      fill={COLOUR_HEX[colour]}
      stroke={STROKE}
      strokeWidth="0.8"
    />
  )
}

/** Right-angle bracket ("⌐"), stroked because it is an outline shape. */
function Bracket({ colour }: { colour: FigureColour }) {
  return (
    <path
      d="M4,16 L4,5 L16,5"
      fill="none"
      stroke={COLOUR_HEX[colour]}
      strokeWidth="3"
    />
  )
}

/** Open semicircular arc — its opening direction makes rotation visible. */
function Arc({ colour }: { colour: FigureColour }) {
  return (
    <path
      d="M3,14 A7,7 0 0 1 17,14"
      fill="none"
      stroke={COLOUR_HEX[colour]}
      strokeWidth="3"
      strokeLinecap="round"
    />
  )
}

function Square({ colour }: { colour: FigureColour }) {
  return <rect x="3" y="3" width="14" height="14" fill={COLOUR_HEX[colour]} stroke={STROKE} strokeWidth="0.8" />
}

function Diamond({ colour }: { colour: FigureColour }) {
  return <polygon points="10,1 19,10 10,19 1,10" fill={COLOUR_HEX[colour]} stroke={STROKE} strokeWidth="0.8" />
}

function Hexagon({ colour }: { colour: FigureColour }) {
  return (
    <polygon
      points="18,10 14,16.9 6,16.9 2,10 6,3.1 14,3.1"
      fill={COLOUR_HEX[colour]}
      stroke={STROKE}
      strokeWidth="0.8"
    />
  )
}

/** One figure drawn into a 20×20 cell. Rotation is applied by the caller
 * around the cell centre — only for shapes whose rotation is visible (the
 * star, square, diamond and hexagon are rotationally symmetric and never
 * rotate, EXAM.md §3 style). */
export function FigureShapeGlyph({ shape, colour }: { shape: FigureShape; colour: FigureColour }) {
  if (shape === 'star') return <Star colour={colour} />
  if (shape === 'triangle') return <Triangle colour={colour} />
  if (shape === 'arrow') return <Arrow colour={colour} />
  if (shape === 'bracket') return <Bracket colour={colour} />
  if (shape === 'arc') return <Arc colour={colour} />
  if (shape === 'square') return <Square colour={colour} />
  if (shape === 'diamond') return <Diamond colour={colour} />
  return <Hexagon colour={colour} />
}
